import { useLocation, useParams, Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useBranding } from "../../hooks/useBranding";
import { useBreadcrumbContext } from "../../hooks/useBreadcrumbContext";

/** Route segment → human-readable label map */
const ROUTE_LABELS: Record<string, string> = {
  "": "Dashboard",
  projects: "Projects",
  new: "New Project",
  activity: "Activity",
  team: "Team",
  "org-settings": "Organization",
  settings: "Settings",
  billing: "Billing",
  "super-admin": "Platform Admin",
  orgs: "Organizations",
};

export function Breadcrumbs() {
  const location = useLocation();
  const params = useParams();
  const { branding } = useBranding();
  const { labels } = useBreadcrumbContext();

  const orgName = branding.company_name || branding.app_name;

  // Build breadcrumb segments from pathname
  const segments = location.pathname.split("/").filter(Boolean);

  // Don't show breadcrumbs on dashboard root
  if (segments.length === 0) {
    return (
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <ol className="breadcrumb-list">
          <li className="breadcrumb-item breadcrumb-root">
            <Home size={14} />
            <span>{orgName}</span>
          </li>
          <li className="breadcrumb-separator" aria-hidden="true">
            <ChevronRight size={14} />
          </li>
          <li className="breadcrumb-item breadcrumb-current">Dashboard</li>
        </ol>
      </nav>
    );
  }

  // Build crumb chain
  const crumbs: { label: string; path: string | null }[] = [
    { label: orgName, path: "/" },
  ];

  let accPath = "";
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    accPath += `/${seg}`;

    // If this segment is a dynamic param (project ID), use context label or truncated ID
    if (params.projectId && seg === params.projectId) {
      // Check if a child page has published a human-readable label for this segment
      const contextLabel = labels[seg];
      const displayLabel = contextLabel
        ? (contextLabel.length > 30 ? `${contextLabel.slice(0, 28)}…` : contextLabel)
        : (seg.length > 20 ? `${seg.slice(0, 8)}…` : seg);
      crumbs.push({ label: displayLabel, path: null });
    } else {
      const label = ROUTE_LABELS[seg] || seg.charAt(0).toUpperCase() + seg.slice(1);
      const isLast = i === segments.length - 1;
      crumbs.push({ label, path: isLast ? null : accPath });
    }
  }

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol className="breadcrumb-list">
        {crumbs.map((crumb, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === crumbs.length - 1;

          return (
            <li key={idx} className="breadcrumb-segment">
              {idx > 0 && (
                <span className="breadcrumb-separator" aria-hidden="true">
                  <ChevronRight size={14} />
                </span>
              )}
              {isLast || !crumb.path ? (
                <span className={`breadcrumb-item breadcrumb-current ${isFirst ? "breadcrumb-root" : ""}`}>
                  {isFirst && <Home size={14} />}
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className={`breadcrumb-item breadcrumb-link ${isFirst ? "breadcrumb-root" : ""}`}
                >
                  {isFirst && <Home size={14} />}
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
